import classNames from 'classnames';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { Container } from 'react-bootstrap';
import { Link as ScrollLink } from 'react-scroll';
import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';


// context
import { useLayout, useLayoutUpdate } from '../../context/LayoutProvider/LayoutProvider';


// components
import NioIcon from '../NioIcon/NioIcon';
import NioBrand from '../NioBrand/NioBrand';
import NioButton from '../NioButton/NioButton';
import useRouteMatch from '../../hooks/useRouteMatch';

// Shared context for open submenu state
const SubMenuContext = createContext({ openSub: null, setOpenSub: () => {}, menus: [] });

export default function Header({ data, className }) {

  const [isFixed, setIsFixed] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const headerRef = useRef(null);
  const location = useLocation();

  // Update CSS variable for header height
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          '--header-height',
          `${headerRef.current.offsetHeight}px`
        );
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isFixed]);

  // components classes
  const compClasses = classNames({
    "nl-header": true,
    "has-fixed": isFixed,
    [`${className}`]: className
  });

  const navClasses = classNames({
    "border-bottom": useRouteMatch(["/", "/index-analytics", "/index-project-management"])
  });

  const handleScroll = () => {
    const shouldFixHeader = window.scrollY > 60;
    setIsFixed(shouldFixHeader);
  }

  // navbar on scroll
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.has-sub') && !e.target.closest('.navbar-sub-panel')) {
        setOpenSub(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenSub(null);
  }, [location.pathname]);

  // Find the active submenu data
  const activeSubData = openSub !== null && data.menus[openSub]
    ? data.menus[openSub].item.submenu
    : null;

  return (
    <SubMenuContext.Provider value={{ openSub, setOpenSub, menus: data.menus }}>
      <header ref={headerRef} className={compClasses}>
        <Navbar expand="lg" className={navClasses}>
          <Container>
            <Header.Logo />
            <Header.Menu data={data.menus} />
            <Header.Action/>
          </Container>
        </Navbar>
        {/* Dropdown panel rendered OUTSIDE navbar, as sibling */}
        <div className={`navbar-sub-panel${openSub !== null && activeSubData && (activeSubData.items.length > 0 || activeSubData.columns?.length > 0) ? ' is-open' : ''}`}>
          <Container key={openSub}>
            {activeSubData && (activeSubData.items.length > 0 || activeSubData.columns?.length > 0) && (() => {
              // Column layout (e.g. Platform)
              if (activeSubData.columns?.length > 0) {
                const hasSideBoxes = activeSubData.sideBoxes?.length > 0;

                if (hasSideBoxes) {
                  // Side boxes layout (e.g. Solutions) — columns left, boxes right
                  return (
                    <div className="row navbar-sub-columns" style={{ flexWrap: 'nowrap' }}>
                      <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <div className="row">
                          {activeSubData.columns.map((col, ci) => (
                            <div key={ci} className={`col-4 navbar-sub-col-${ci + 1}`}>
                              <h6 className="navbar-sub-col-heading">{col.title}</h6>
                              <ul className="list-unstyled mb-0">
                                {col.items.map((item, ii) => (
                                  <li key={ii}>
                                    <Link to={item.url} className="navbar-sub-col-link">
                                      {item.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ flex: '0 0 auto', width: 'clamp(200px, 22vw, 320px)', display: 'flex', flexDirection: 'column', gap: '0.75rem', marginRight: 'clamp(-2rem, -3.5vw, -5rem)' }}>
                        {activeSubData.sideBoxes.map((box, bi) => (
                          <Link key={bi} to={box.url} className={`navbar-sub-banner navbar-sub-sidebox-${bi + 1}`} style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h6 className="navbar-sub-banner-title">{box.title}</h6>
                            <p className="navbar-sub-banner-sub">{box.sub}</p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Columns left, banners right (same as Solutions side boxes)
                return (
                  <div className="row navbar-sub-columns" style={{ flexWrap: 'nowrap' }}>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <div className="row">
                        {activeSubData.columns.map((col, ci) => {
                          const colCount = activeSubData.columns.length;
                          const colClass = colCount > 3 ? '' : `col-4`;
                          const colStyle = colCount > 3 ? { flex: ci < colCount - 1 ? '1 1 0' : '0 0 auto', width: ci < colCount - 1 ? undefined : '18%', minWidth: 0 } : {};
                          return (
                            <div key={ci} className={`${colClass} navbar-sub-col-${ci + 1}`} style={colStyle}>
                              <h6 className="navbar-sub-col-heading">{col.title}</h6>
                              <ul className="list-unstyled mb-0">
                                {col.items.map((item, ii) => (
                                  <li key={ii}>
                                    <Link to={item.url} className="navbar-sub-col-link">
                                      {item.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {!activeSubData.noBanners && (
                      <div style={{ flex: '0 0 auto', width: 'clamp(200px, 22vw, 320px)', display: 'flex', flexDirection: 'column', gap: '0.75rem', marginRight: 'clamp(-2rem, -3.5vw, -5rem)' }}>
                        <Link to="/how-it-works" className="navbar-sub-banner navbar-sub-sidebox-1" style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                          <div style={{
                            position: 'absolute',
                            right: '-50%',
                            top: '-200%',
                            bottom: '-200%',
                            aspectRatio: '1 / 1',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                            zIndex: 0,
                          }}>
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              transformOrigin: '50% 50%',
                              background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.08) 15deg, transparent 30deg, rgba(255,255,255,0.06) 55deg, transparent 70deg, rgba(255,255,255,0.08) 90deg, transparent 105deg, rgba(255,255,255,0.1) 125deg, transparent 140deg, rgba(255,255,255,0.08) 160deg, transparent 175deg, rgba(255,255,255,0.1) 195deg, transparent 210deg, rgba(255,255,255,0.08) 230deg, transparent 245deg, rgba(255,255,255,0.06) 265deg, transparent 280deg, rgba(255,255,255,0.08) 305deg, transparent 320deg, rgba(255,255,255,0.06) 345deg, transparent 360deg)',
                              animation: 'fanRotate 18s linear infinite',
                            }} />
                          </div>
                          <h6 className="navbar-sub-banner-title" style={{ position: 'relative', zIndex: 1 }}>Platform Overview</h6>
                          <p className="navbar-sub-banner-sub" style={{ position: 'relative', zIndex: 1 }}>Explore origin intelligence</p>
                        </Link>
                        <Link to="/where-ai-matters" className="navbar-sub-banner navbar-sub-sidebox-2" style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                          <div style={{
                            position: 'absolute',
                            right: '-50%',
                            top: '-200%',
                            bottom: '-200%',
                            aspectRatio: '1 / 1',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                            zIndex: 0,
                          }}>
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              transformOrigin: '50% 50%',
                              background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.08) 15deg, transparent 30deg, rgba(255,255,255,0.06) 55deg, transparent 70deg, rgba(255,255,255,0.08) 90deg, transparent 105deg, rgba(255,255,255,0.1) 125deg, transparent 140deg, rgba(255,255,255,0.08) 160deg, transparent 175deg, rgba(255,255,255,0.1) 195deg, transparent 210deg, rgba(255,255,255,0.08) 230deg, transparent 245deg, rgba(255,255,255,0.06) 265deg, transparent 280deg, rgba(255,255,255,0.08) 305deg, transparent 320deg, rgba(255,255,255,0.06) 345deg, transparent 360deg)',
                              animation: 'fanRotate 25s linear infinite reverse',
                            }} />
                          </div>
                          <h6 className="navbar-sub-banner-title" style={{ position: 'relative', zIndex: 1 }}>AI in Coffee</h6>
                          <p className="navbar-sub-banner-sub" style={{ position: 'relative', zIndex: 1 }}>Evolve your experience</p>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }
              // Flat icon layout (Resources, Solutions, etc.)
              const mainItems = activeSubData.items.filter(i => i.title !== 'Support' && i.title !== 'Forum');
              const sideItems = activeSubData.items.filter(i => i.title === 'Support' || i.title === 'Forum');
              return (
                <div className="row">
                  <div className="col-9">
                    <ul className="row g-1">
                      {mainItems.map((item, idx) => (
                        <li key={idx} className={`col-4 navbar-sub-col-${(idx % 3) + 1}`}>
                          <NavLink to={item?.url} className="navbar-sub-item">
                            <NioIcon className="fs-4" name={item.icon} variant={item.color} />
                            <div className="w-100">
                              <h5 className="navbar-sub-title d-flex align-items-center justify-content-between mb-0">
                                {item.title}
                                {item.isNew &&
                                  <span className="fs-9 fw-semibold text-primary text-uppercase">New</span>
                                }
                              </h5>
                              {item.subtitle && <span className="navbar-sub-subtitle">{item.subtitle}</span>}
                            </div>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {sideItems.length > 0 && (
                    <div className="col-3 navbar-sub-side">
                      <ul className="list-unstyled">
                        {sideItems.map((item, idx) => (
                          <li key={idx}>
                            <NavLink to={item?.url} className="navbar-sub-item">
                              <NioIcon className="fs-4" name={item.icon} variant={item.color} />
                              <div className="w-100">
                                <h5 className="navbar-sub-title mb-0">{item.title}</h5>
                                {item.subtitle && <span className="navbar-sub-subtitle">{item.subtitle}</span>}
                              </div>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </Container>
        </div>
      </header>
    </SubMenuContext.Provider>
  )
}


function HeaderLogo() {
  return (
    <Link to="/" className="logo-link navbar-brand">
      <div className="logo-wrap">
        <img className="logo-img" src="images/ws-icon-green.webp" alt="Weathersafe" style={{ width: '40px', minWidth: '40px', height: 'auto', objectFit: 'contain' }} />
      </div>
    </Link>
  )
}


function HeaderMenu({ data }) {

  const compClasses = classNames({
    "nl-header-menu nl-navbar ms-4": true,
  });

  return (
    <Navbar.Collapse className="d-none d-lg-block">
      <nav className={compClasses}>
        <Header.Nav data={data} />
        <div className="nl-navbar-btn d-lg-none">
          <ul className="d-flex flex-wrap gap-3 justify-content-center">
            <li className="w-100">
              <NioButton
                icon="bag-fill"
                className="btn-primary"
                label="Purchase Now"
                href="https://1.envato.market/NioLandReact"
              />
            </li>
          </ul>
        </div>
      </nav>
    </Navbar.Collapse>
  );
}


function HeaderNav({ data }) {

  const location = useLocation();
  const { openSub, setOpenSub } = useContext(SubMenuContext);

  const handleToggle = (e, idx) => {
    e.preventDefault();
    setOpenSub(openSub === idx ? null : idx);
  };

  return (
    <Nav as="ul"  className="nl-nav">
      {
        data && data.map((menu, idx) => {
          const hasSub = menu.item.submenu.items.length !== 0 || (menu.item.submenu.columns?.length ?? 0) > 0;
          const isOpen = openSub === idx;

          return (
          <Nav.Item
            as="li"
            key={idx}
            className={`${hasSub ? ' has-sub' : ''}${isOpen ? ' sub-open' : ''}${idx === data.length - 2 ? ' nl-nav-separated' : ''}${idx === data.length - 1 ? ' nl-nav-cta' : ''}`}>
            {
              location.pathname === "/" ? (
                menu.item.url.startsWith("#") ? (
                  <ScrollLink
                    offset={-60}
                    to={menu.item.url.slice(1)}
                    className={`nav-link ${hasSub ? 'nav-toggle' : ''}`}
                    onClick={hasSub ? (e) => handleToggle(e, idx) : undefined}
                  >
                    {menu.item.title}
                  </ScrollLink>
                ) :
                  (
                    <Link
                      to={hasSub ? '#' : menu.item.url}
                      className={`nav-link ${hasSub ? 'nav-toggle' : ''}`}
                      onClick={hasSub ? (e) => handleToggle(e, idx) : undefined}
                    >
                      {menu.item.title}
                    </Link>
                  )
              ) : (
                <Link
                  to={hasSub ? '#' : menu.item.url}
                  className={`nav-link ${hasSub ? 'nav-toggle' : ''}`}
                  onClick={hasSub ? (e) => handleToggle(e, idx) : undefined}
                >
                  {menu.item.title}
                </Link>
              )
            }
          </Nav.Item>
          );
        })
      }
    </Nav>
  )
}



function HeaderAction() {

  const layoutUpdate = useLayoutUpdate();

  return (
    <div className="nl-header-action">
      <ul className="d-flex flex-wrap gap-3 gap-3 justify-content-center">
        <li className="d-lg-none">
          <Navbar.Toggle className="btn-block" onClick={layoutUpdate.sideBarAction} />
        </li>
      </ul>
    </div>
  )
}

Header.Nav = HeaderNav;
Header.Logo = HeaderLogo;
Header.Menu = HeaderMenu;
Header.Action = HeaderAction;
